import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Screensaver from '@/booth/screensaver/Screensaver'
import { SCREENSAVER_STYLES, type ScreensaverStyle } from '../server/lib/eventSettings'
import { useI18n } from '@/lib/i18n'

/** Video paths the screensaver setting accepts; anything else falls back to the default. */
const SAFE_VIDEO = /^\/(uploads|video|default-assets)\/[a-zA-Z0-9_./% -]+$/

function phrasesFromHash(): string[] | undefined {
  try {
    const raw = new URLSearchParams(window.location.hash.slice(1)).get('p')
    if (!raw) return undefined
    const list = JSON.parse(raw)
    return Array.isArray(list) ? list.filter((p): p is string => typeof p === 'string').slice(0, 100) : undefined
  } catch {
    return undefined
  }
}

/**
 * Full-screen screensaver preview: /screensaver-preview?style=party
 * Number keys or arrow keys switch style; the switcher hides after a few idle seconds.
 * `ui=0` hides the switcher entirely (used by the admin live preview).
 */
export default function ScreensaverPreview() {
  const { t } = useI18n()
  const [params, setParams] = useSearchParams()
  const style = (SCREENSAVER_STYLES as readonly string[]).includes(params.get('style') || '') ? params.get('style') as ScreensaverStyle : 'shutter'
  const video = params.get('video') || ''
  const showUi = params.get('ui') !== '0'
  const wake = params.get('wake') === 'motion' ? 'motion' : 'touch'
  const [phrases] = useState(phrasesFromHash)
  const [uiVisible, setUiVisible] = useState(true)
  const hideTimer = useRef<ReturnType<typeof setTimeout>>()

  const select = (next: ScreensaverStyle) => {
    const p = new URLSearchParams(params)
    p.set('style', next)
    setParams(p, { replace: true })
  }

  useEffect(() => {
    const poke = () => {
      setUiVisible(true)
      clearTimeout(hideTimer.current)
      hideTimer.current = setTimeout(() => setUiVisible(false), 3000)
    }
    const onKey = (e: KeyboardEvent) => {
      poke()
      const i = SCREENSAVER_STYLES.indexOf(style)
      const n = Number(e.key)
      if (n >= 1 && n <= SCREENSAVER_STYLES.length) select(SCREENSAVER_STYLES[n - 1])
      else if (e.key === 'ArrowRight') select(SCREENSAVER_STYLES[(i + 1) % SCREENSAVER_STYLES.length])
      else if (e.key === 'ArrowLeft') select(SCREENSAVER_STYLES[(i - 1 + SCREENSAVER_STYLES.length) % SCREENSAVER_STYLES.length])
    }
    poke()
    window.addEventListener('pointermove', poke)
    window.addEventListener('keydown', onKey)
    return () => {
      clearTimeout(hideTimer.current)
      window.removeEventListener('pointermove', poke)
      window.removeEventListener('keydown', onKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [style, params])

  return (
    <div className="fixed inset-0 overflow-hidden bg-black" style={{ cursor: uiVisible ? 'default' : 'none' }}>
      <Screensaver key={style} style={style} videoSrc={SAFE_VIDEO.test(video) && !video.includes('..') ? video : undefined} phrases={phrases} wakeMethod={wake} />
      {showUi && (
        <div
          className="fixed left-1/2 top-4 z-[10001] flex -translate-x-1/2 gap-1 rounded-full border border-white/15 bg-black/70 p-1 text-sm text-white shadow-2xl backdrop-blur-md transition-opacity duration-500"
          style={{ opacity: uiVisible ? 1 : 0, pointerEvents: uiVisible ? 'auto' : 'none', fontFamily: "'Geist Variable', sans-serif" }}
        >
          {SCREENSAVER_STYLES.map((s, i) => (
            <button
              key={s}
              onClick={() => select(s)}
              className={`rounded-full px-4 py-2 font-medium transition-colors ${s === style ? 'bg-white text-black' : 'text-white/75 hover:bg-white/10 hover:text-white'}`}
            >
              <span className="mr-1.5 font-mono text-xs opacity-50">{i + 1}</span>
              {t(`screensaver.styles.${s}.name`)}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
